import React, { FC, Fragment, useContext, useState } from "react";
import { ExternalChatOrganization, ExternalChatUserState, } from "domain-entities";
import SidebarView from "Components/Layout/SidebarView";
import ContentView from "Components/Layout/ContentView";
import OrganizationsView from "./OrganizationsView";
import { useOrganizationsStore } from "./OrganizationsStore";
import { SaveDocumentStore } from "Helpers/StoreHelper";
import ChannelsPage from "Components/Channel/ChannelsPage";
import { isOrganizationAdded } from "./OrganizationHelper";
import OrganizationSuggestionCtaView from "../OrganizationSuggestion/OrganizationSuggestionCtaView";
import { Box, Fab, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { px } from "Configs/theme";
import OrganizationsAddableModal from "./OrganizationsAddableModal";
import { useModalController } from "Components/Modal/Modal";
import LoadingIndicator from "Components/Layout/LoadingIndicator";
import { TrackingEvent, useTracking } from "../../Providers/TrackingProvider";
import InfoBox from "../InfoBox/InfoBox";
import FadeInBox from "../Layout/FadeInBox";
import { useTranslation } from "../../Providers/TranslationProvider";
import { InitContext } from "../../Providers/InitProvider";

interface Props {
  viewerUserStateStore: SaveDocumentStore<ExternalChatUserState>;
}

const OrganizationsPage: FC<Props> = (props) => {
  const organizationsStore = useOrganizationsStore();
  const classes = useStyles();
  const organizationsAddableMc = useModalController<ExternalChatOrganization>();
  const viewerUserState = props.viewerUserStateStore.item;
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<ExternalChatOrganization>();
  const unreadOrganizationsIds: string[] = [];

  const addedOrganizations: ExternalChatOrganization[] = [];
  const addableOrganizations: ExternalChatOrganization[] = [];
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const [isInfoVisible, setInfoVisibility] = useState(true);
  const initContext = useContext(InitContext)

  organizationsStore.items.forEach((organization) => {
    if (viewerUserState.organizationsAdded.includes(organization.id)) {
      addedOrganizations.push(organization);
    } else {
      addableOrganizations.push(organization);
    }
  });

  addedOrganizations.forEach(addedOrganization => {
    if (viewerUserState.unreadChannels.some(unreadChannel => unreadChannel.startsWith(addedOrganization.id))) {
      unreadOrganizationsIds.push(addedOrganization.id);
    }
  });


  function openInitialOrganization() {
    if (organizationsStore.isLoading || !initContext.organizationId) {
      return;
    }
    const organization = organizationsStore.items.find(organization => organization.id === initContext.organizationId);
    const userHasAddedInitOrganization = addedOrganizations.find(organization => organization.id === initContext.organizationId)
    initContext.resetOrganizationId();
    if (userHasAddedInitOrganization) {
      setSelectedOrganization(organization);
    } else if (organization) {
      openOrganizationsModal(organization);
    }
  }

  openInitialOrganization();

  function openOrganizationsModal(organization?: ExternalChatOrganization): void {
    trackEvent(TrackingEvent.FAB_CLICKED);
    organizationsAddableMc.open(organization);
  }

  function onInfoClose() {
    setInfoVisibility(false);
  }

  function renderFab() {
    return (<Box className={classes.fab}>
        {isInfoVisible && !addedOrganizations.length && (
          <FadeInBox className={classes.info}>
            <InfoBox direction="bottomRight" onClose={onInfoClose} title={t("OrganizationsInfo_Title")}>
              <Box>
                {t("OrganizationsInfo_Body")}
              </Box>
            </InfoBox>
          </FadeInBox>
        )}
        <Fab
          color="primary"
          onClick={() => openOrganizationsModal()}
        >
          <AddIcon/>
        </Fab></Box>
    );
  }

  if (isOrganizationAdded(viewerUserState, selectedOrganization)) {
    return (
      <ChannelsPage
        organization={selectedOrganization}
        viewerUserStateStore={props.viewerUserStateStore}
        onClose={() => setSelectedOrganization(undefined)}
      />
    );
  } else if (organizationsStore.isLoading) {
    return <LoadingIndicator/>;
  } else {
    return (
      <Fragment>
        <SidebarView fab={renderFab()}>
          <OrganizationsView
            organizations={addedOrganizations}
            setSelectedOrganization={setSelectedOrganization}
            unreadOrganizations={unreadOrganizationsIds}
          />
        </SidebarView>
        <ContentView>
          <OrganizationSuggestionCtaView
            buttonVariant="contained"
            padding={px.l}
          />
        </ContentView>
        <OrganizationsAddableModal
          organizations={addableOrganizations}
          viewerUserStateStore={props.viewerUserStateStore}
          modalController={organizationsAddableMc}
        />
      </Fragment>
    );
  }
};

export default OrganizationsPage;

const useStyles = makeStyles({
  fab: {
    position: "absolute",
    bottom: px.l,
    right: px.l,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
  },
  info: {
    width: "300px",
    marginBottom: px.xl
  }
});
